import React, { BaseSyntheticEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { closeCircleOutline } from 'ionicons/icons'
import { IonButton, IonButtons, IonChip, IonCol, IonIcon, IonItem, IonLabel, IonRow } from '@ionic/react'
import { AccordianComponent } from '../../../components/accordian/Accordian.components'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import SInput from '../../../components/stringInput/StringInput.component'
import Validation from '../../../components/validation/Validation.component'
import { IOption } from '../../../common/common.types'
import { getPatientDemographicsOptionsSelector } from '../../options/selectors'
import { getPatientDemographicsSelector } from '../selectors'
import {
  addPhysicianData,
  addPhysicianList,
  clearPhysicianData,
  savePatientDemographics,
  setPhysicianData,
  setPhysicianFirstName,
  setPhysicianLastName,
  setPhysicianMiddleName,
  setPhysicianType,
} from '../store/patientDemographics.actions'
import { IPhysicianDetail } from '../store/patientDemographics.types'
import { PatientDemographicsConstants } from '../store/patientDemographics.constants'
import { clearValidationErrorsByPathList } from '../../../utilities/runRulesValidation.util'
import { IRuleError, IRulesState } from '../../rules/store/rules.types'
import { getRulesSelector } from '../../rules/selectors'
import './physicianDetails.component.css'

const PhysicianInformation: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const patientDemographics = useSelector(getPatientDemographicsSelector)
  const patientDemographicsOptions = useSelector(getPatientDemographicsOptionsSelector)
  const rules: IRulesState = useSelector(getRulesSelector)
  const { errors } = rules
  const { physicianTypeOptions } = patientDemographicsOptions
  const { physicianData, physicianDataList } = patientDemographics
  const { physicianFirstName, physicianLastName, physicianMiddleName, physicianType } = physicianData
  const enableSave = !physicianFirstName && !physicianLastName && !physicianType?.id
  const enableClear = !physicianFirstName && !physicianLastName && !physicianMiddleName && !physicianType?.id

  const setFirstNameChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setPhysicianFirstName(e.detail.value))
    dispatch(savePatientDemographics())
  }

  const setLastNameChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setPhysicianLastName(e.detail.value))
    dispatch(savePatientDemographics())
  }

  const setMiddleNameChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setPhysicianMiddleName(e.detail.value))
    dispatch(savePatientDemographics())
  }

  const setPhysicianTypeChange = (type: IOption) => {
    dispatch(setPhysicianType(type))
    dispatch(savePatientDemographics())
  }

  const onAddPhysicianDetails = () => {
    const reduxActions: string[] = [
      PatientDemographicsConstants.SET_PHYSICIAN_LASTNAME,
      PatientDemographicsConstants.SET_PHYSICIAN_FIRSTNAME,
      PatientDemographicsConstants.SET_PHYSICIAN_MIDDLENAME,
      'state_patientDemographics/SET_PHYSICIAN_LASTNAME',
      'state_patientDemographics/SET_PHYSICIAN_FIRSTNAME',
      'state_patientDemographics/SET_PHYSICIAN_MIDDLENAME',
    ]

    const validationErrors = Object.values(errors)
      .flat()
      .filter((error: IRuleError) => reduxActions.includes(error.id) && error.reduxStateOnlyFlag)

    if (validationErrors.length) {
      alert('Please Correct Errors')
      return
    }

    dispatch(addPhysicianData())
    clearValidations()
    dispatch(setPhysicianFirstName(''))
    dispatch(setPhysicianLastName(''))
    dispatch(setPhysicianMiddleName(''))
  }

  const removeChip = (e: BaseSyntheticEvent, option: IPhysicianDetail) => {
    e.stopPropagation()
    const updatedPhysicianData = [...physicianDataList]
    const index = updatedPhysicianData.findIndex((opt) => opt.key === option.key)
    if (index !== -1) {
      updatedPhysicianData.splice(index, 1)
    }
    dispatch(addPhysicianList(updatedPhysicianData))
    dispatch(savePatientDemographics())
  }

  const onClearPhysician = () => {
    dispatch(clearPhysicianData())
    dispatch(setPhysicianFirstName(''))
    dispatch(setPhysicianLastName(''))
    dispatch(setPhysicianMiddleName(''))
    dispatch(savePatientDemographics())
    clearValidations()
  }

  const getDisplayName = (option: IPhysicianDetail) => {
    if (option.physicianLastName && option.physicianFirstName) {
      return `${option.physicianLastName}, ${option.physicianFirstName.charAt(0)}`
    }
    if (option.physicianLastName) return option.physicianLastName
    if (option.physicianFirstName) return option.physicianFirstName
    return option.physicianType?.name
  }

  const clearValidations = () => {
    rules.errors = clearValidationErrorsByPathList(rules.errors, [
      PatientDemographicsConstants.SET_PHYSICIAN_LASTNAME,
      PatientDemographicsConstants.SET_PHYSICIAN_FIRSTNAME,
      PatientDemographicsConstants.SET_PHYSICIAN_MIDDLENAME,
      'state_patientDemographics/SET_PHYSICIAN_LASTNAME',
      'state_patientDemographics/SET_PHYSICIAN_FIRSTNAME',
      'state_patientDemographics/SET_PHYSICIAN_MIDDLENAME',
    ])
  }

  const handleChipClick = (e: BaseSyntheticEvent, physicianData: IPhysicianDetail) => {
    e.stopPropagation()
    const physicianInfo = physicianDataList.find((pData) => pData.key === physicianData.key)
    if (physicianInfo) {
      dispatch(setPhysicianFirstName(physicianInfo.physicianFirstName))
      dispatch(setPhysicianLastName(physicianInfo.physicianLastName))
      dispatch(setPhysicianMiddleName(physicianInfo.physicianMiddleName))
      dispatch(setPhysicianData(physicianInfo))
      dispatch(savePatientDemographics())
    }
  }
  const physicianContent = (
    <>
      {physicianDataList.length > 0 && (
        <IonItem lines='none'>
          <IonRow className='ion-nowrap no-flex-shrink' id='Dnr-list-row'>
            {physicianDataList.map((option: IPhysicianDetail) => (
              <IonChip
                data-testid={`Chip${option.key}`}
                key={`Chip_${option.key}${option.physicianFirstName}`}
                onClick={(e) => handleChipClick(e, option)}
                className='lookup-search-chips custom-background'>
                <IonLabel className='label-width'>{getDisplayName(option)}</IonLabel>
                <IonIcon color='white' icon={closeCircleOutline} onClick={(e) => removeChip(e, option)} />
              </IonChip>
            ))}
          </IonRow>
        </IonItem>
      )}
      <IonRow>
        <IonCol className='picklist'>
          <SInput
            isPHI
            label={t('patientDemographics.physicianDetail.LastName')}
            dataTestID='PhysicianLastName'
            inputValue={physicianLastName}
            onChangeAction={setLastNameChange}
          />
          <Validation id={PatientDemographicsConstants.SET_PHYSICIAN_LASTNAME} />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />
      <IonRow>
        <IonCol className='picklist'>
          <SInput
            isPHI
            label={t('patientDemographics.physicianDetail.FirstName')}
            dataTestID='PhysicianFirstName'
            inputValue={physicianFirstName}
            onChangeAction={setFirstNameChange}
          />
          <Validation id={PatientDemographicsConstants.SET_PHYSICIAN_FIRSTNAME} />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />
      <IonRow>
        <IonCol className='picklist'>
          <SInput
            isPHI
            label={t('patientDemographics.physicianDetail.Middle')}
            dataTestID='PhysicianMiddleName'
            inputValue={physicianMiddleName}
            onChangeAction={setMiddleNameChange}
          />
          <Validation id={PatientDemographicsConstants.SET_PHYSICIAN_MIDDLENAME} />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />
      <IonRow>
        <IonCol className='picklist'>
          <SingleSelect
            dataTestID='PhysicianType'
            selectedOption={physicianType}
            listHeader={t('patientDemographics.physicianDetail.Type')}
            label={t('patientDemographics.physicianDetail.Type')}
            options={physicianTypeOptions}
            action={setPhysicianTypeChange}
          />
        </IonCol>
      </IonRow>
      <IonRow>
        <IonCol>
          <IonButtons className='ion-float-right '>
            <IonButton
              disabled={enableClear}
              className='btn btn-standard mr-8'
              color='danger'
              fill='outline'
              expand='block'
              onClick={() => onClearPhysician()}>
              {t('common.buttons.Clear')}
            </IonButton>
            <IonButton
              disabled={enableSave}
              className='btn btn-standard btn-primary'
              expand='block'
              onClick={() => onAddPhysicianDetails()}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonCol>
      </IonRow>
    </>
  )

  return (
    <AccordianComponent
      dataTestId={'Physicians'}
      heading={'Physicians'}
      content={physicianContent}
      isCheckMark={!enableClear || physicianDataList.length !== 0}
    />
  )
}

export default PhysicianInformation
